// PaymentDetails Component - Shows payment information for application
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import {
    Payment as PaymentIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Receipt as ReceiptIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';
import DoApiService from '../../../services/DoApiService';

const PaymentDetails = ({ appId }) => {
    // State management
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [paymentDetails, setPaymentDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paymentTypesLoading, setPaymentTypesLoading] = useState(true);

    // Filter states
    const [selectedPaymentType, setSelectedPaymentType] = useState('');
    const [orderIdFilter, setOrderIdFilter] = useState('');

    // Load payment types on component mount
    useEffect(() => {
        loadPaymentTypes();
    }, []);

    // Load payment details when appId changes or filters are applied
    useEffect(() => {
        if (appId) {
            loadPaymentDetails();
        }
    }, [appId, selectedPaymentType, orderIdFilter]);

    // Load payment types for dropdown
    const loadPaymentTypes = async () => {
        try {
            setPaymentTypesLoading(true);
            console.log('Loading payment types...');

            const response = await DoApiService.getPaymentTypes();
            
            if (response.success) {
                setPaymentTypes(response.data || []);
                console.log('Payment types loaded:', response.data?.length || 0);
            } else {
                console.error('Failed to load payment types:', response.message);
                setError('Failed to load payment types');
            }
        } catch (error) {
            console.error('Error loading payment types:', error);
            setError('Error loading payment types: ' + error.message);
        } finally {
            setPaymentTypesLoading(false);
        }
    };

    // Load payment details
    const loadPaymentDetails = async () => {
        if (!appId) return;

        try {
            setLoading(true);
            setError(null);
            console.log('Loading payment details for appId:', appId);

            const response = await DoApiService.getPaymentDetails({
                appId: parseInt(appId),
                paymentTypeId: selectedPaymentType || null,
                orderId: orderIdFilter || null
            });
            
            if (response.success) {
                setPaymentDetails(response.data || []);
                console.log('Payment details loaded:', response.data?.length || 0);
            } else {
                console.error('Failed to load payment details:', response.message);
                setError('Failed to load payment details');
            }
        } catch (error) {
            console.error('Error loading payment details:', error);
            setError('Error loading payment details: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle filter changes
    const handlePaymentTypeChange = (event) => {
        setSelectedPaymentType(event.target.value);
    };

    const handleOrderIdChange = (event) => {
        setOrderIdFilter(event.target.value);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSelectedPaymentType('');
        setOrderIdFilter('');
    };

    // Format currency
    const formatCurrency = (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Get status chip color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    if (!appId) {
        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" color="textSecondary" sx={{ textAlign: 'center' }}>
                        No application selected for payment details
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ 
            borderRadius: 3,
            mt: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0'
        }}>
            <CardContent sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2,
                        p: 1.5,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <PaymentIcon sx={{ fontSize: 24, color: 'white' }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                            Payment Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View and track payment transactions
                        </Typography>
                    </Box>
                </Box>

                {/* Filters Section */}
                <Paper sx={{ 
                    p: 3, 
                    mb: 3, 
                    bgcolor: '#f8fafc', 
                    border: '1px solid #e2e8f0',
                    borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Filter Options
                    </Typography>
                    <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Transaction Type</InputLabel>
                            <Select
                                value={selectedPaymentType}
                                onChange={handlePaymentTypeChange}
                                label="Transaction Type"
                                disabled={paymentTypesLoading}
                            >
                                <MenuItem value="">
                                    <em>All Transaction Types</em>
                                </MenuItem>
                                {paymentTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Order ID"
                            placeholder="Enter Order ID to search"
                            value={orderIdFilter}
                            onChange={handleOrderIdChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            startIcon={<ClearIcon />}
                            onClick={handleClearFilters}
                            disabled={!selectedPaymentType && !orderIdFilter}
                            sx={{ 
                                height: '40px',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 6px 16px rgba(240, 147, 251, 0.4)'
                                }
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
                </Paper>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Loading payment details...
                        </Typography>
                    </Box>
                )}

                {/* Payment Details Table */}
                {!loading && (
                    <>
                        {paymentDetails.length > 0 ? (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    borderRadius: 2,
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ 
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        }}>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>S.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Application No</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Lab ID</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Order Id</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Fees Type</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Payment Status</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Payment Mode</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Transaction Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', py: 2 }}>Transaction Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentDetails.map((payment, index) => (
                                            <TableRow 
                                                key={payment.orderId + index} 
                                                hover
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#f8fafc' },
                                                    '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }
                                                }}
                                            >
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {payment.applicationNo || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {payment.laboratoryName || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <ReceiptIcon sx={{ mr: 1, fontSize: 16, color: 'action.active' }} />
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {payment.orderId || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {payment.Description || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={payment.paymentStatus || 'Unknown'}
                                                        color={getStatusColor(payment.paymentStatus)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {payment.paymentMode || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: 'medium',
                                                            color: 'success.main',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        {formatCurrency(payment.transactionAmount)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(payment.transactionDate)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Paper sx={{ 
                                textAlign: 'center', 
                                py: 6, 
                                px: 4,
                                bgcolor: '#fafbfc',
                                border: '2px dashed #e2e8f0',
                                borderRadius: 2
                            }}>
                                <Box sx={{ 
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '50%',
                                    width: 80,
                                    height: 80,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 3
                                }}>
                                    <PaymentIcon sx={{ fontSize: 40, color: 'white' }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                    No Payment Records Found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                                    {selectedPaymentType || orderIdFilter 
                                        ? 'No payments match the current filters. Try adjusting your search criteria.'
                                        : 'No successful payment transactions found for this application.'
                                    }
                                </Typography>
                            </Paper>
                        )}
                    </>
                )}

                {/* Summary */}
                {!loading && paymentDetails.length > 0 && (
                    <Paper sx={{ 
                        mt: 3, 
                        p: 3, 
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white',
                        borderRadius: 2 
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptIcon sx={{ mr: 2, fontSize: 24 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Total Records: {paymentDetails.length} payment(s) found
                                {(selectedPaymentType || orderIdFilter) && (
                                    <span style={{ opacity: 0.9 }}> (filtered results)</span>
                                )}
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </CardContent>
        </Card>
    );
};

export default PaymentDetails;
